import type { Address } from "viem"
import type { Config } from "wagmi"
import { runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQuery, OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { StrategyManagerStore } from "@/domains/vote-optimizer"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { hasApprovedSuperVoter } from "@/domains/on-chain"
import { NftSelectionChangedEvent } from "@/domains/selected-power/events"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { VotePageStore } from "./vote-page-store"
import { SuperVoterAccessGrantedEvent, SuperVoterAccessRevokedEvent, SuperVoterManagerDelegatedEvent } from "./events"

export class SuperVoterAccessOrchestrator {
  private accessQuery: OrchestratedQuery<boolean> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private accessGrantedHandler: ((event: SuperVoterAccessGrantedEvent) => void) | undefined
  private accessRevokedHandler: ((event: SuperVoterAccessRevokedEvent) => void) | undefined
  private managerDelegatedHandler: ((event: SuperVoterManagerDelegatedEvent) => void) | undefined
  private nftSelectionChangedHandler: ((event: NftSelectionChangedEvent) => void) | undefined
  private strategyManagerStore: StrategyManagerStore | undefined

  constructor(
    private readonly votePageStore: VotePageStore,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly walletStore: IWalletConnectionStore,
    private readonly eventBus: IEventBus,
    private readonly config: Config,
    private readonly chainId: number,
    private readonly veNftAddress: Address,
    private readonly superVoterAddress: Address,
    strategyManagerStore?: StrategyManagerStore,
  ) {
    this.strategyManagerStore = strategyManagerStore
    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)
  }

  initialize(accessQuery: OrchestratedQuery<boolean>) {
    this.accessQuery = accessQuery

    // Subscribe to domain events for immediate refresh after transactions
    this.accessGrantedHandler = this.handleAccessGranted.bind(this)
    this.accessRevokedHandler = this.handleAccessRevoked.bind(this)
    this.managerDelegatedHandler = this.handleManagerDelegated.bind(this)

    this.eventBus.subscribe(SuperVoterAccessGrantedEvent.TYPE, this.accessGrantedHandler)
    this.eventBus.subscribe(SuperVoterAccessRevokedEvent.TYPE, this.accessRevokedHandler)
    this.eventBus.subscribe(SuperVoterManagerDelegatedEvent.TYPE, this.managerDelegatedHandler)

    // Subscribe to NFT selection events for strategy reapply
    this.nftSelectionChangedHandler = this.handleNftSelectionChanged.bind(this)
    this.eventBus.subscribe(NftSelectionChangedEvent.TYPE, this.nftSelectionChangedHandler)
  }

  /**
   * Set the strategy manager store (called after VoteOptimizer is created)
   */
  setStrategyManagerStore(strategyManagerStore: StrategyManagerStore): void {
    this.strategyManagerStore = strategyManagerStore
  }

  /**
   * Get the access query for external use (e.g., ForceRefreshButton)
   */
  get query(): OrchestratedQuery<boolean> | undefined {
    return this.accessQuery
  }

  /**
   * Force execute the access query immediately
   * Used after grant/revoke/delegate operations for instant UI updates
   */
  forceExecute(): void {
    if (this.accessQuery) {
      this.accessQuery.forceExecute()
    }
  }

  /**
   * Trigger function for the OrchestratedQuery
   * Fetches the current access state from the blockchain
   */
  async onCheckAccessTrigger(_query: IOrchestratedQuerySender<boolean>): Promise<boolean> {
    const userAddress = this.walletStore.getConnectedAddress()

    if (!userAddress) {
      return false
    }

    try {
      const access = await hasApprovedSuperVoter({
        account: userAddress,
        chainId: this.chainId,
        config: this.config,
        veNftAddress: this.veNftAddress,
        superVoterAddress: this.superVoterAddress,
      })

      return access
    } catch (error) {
      console.error("SuperVoterAccessOrchestrator.onCheckAccessTrigger", error)
      return false
    }
  }

  /**
   * Callback for when access query succeeds
   */
  onCheckAccessQuerySuccess(_sender: IOrchestratedQuerySender<boolean>, data: boolean): void {
    try {
      runInAction(() => {
        this.votePageStore.setHasSuperVoterAccess(data)
      })
    } catch (error) {
      console.error("Error processing super voter access:", error)
    }
  }

  /**
   * Callback for when access query fails
   */
  onCheckAccessQueryFailed(_sender: IOrchestratedQuerySender<boolean>, error: Error): void {
    // On failure, set access to false
    runInAction(() => {
      this.votePageStore.setHasSuperVoterAccess(false)
    })
    console.error("SuperVoter access query failed:", error)
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Stop the query
    if (this.accessQuery) {
      this.accessQuery.stop()
    }

    // Reset access state and SuperVoter allocations
    runInAction(() => {
      // Save current strategy before resetting (for reapply on NFT selection change)
      const currentStrategy = this.votePageStore.lastAppliedStrategy
      if (currentStrategy) {
        this.votePageStore.setPreviousAppliedStrategy(currentStrategy)
      }
      const currentTokenStrategy = this.votePageStore.lastAppliedTokenStrategy
      if (currentTokenStrategy) {
        this.votePageStore.setPreviousAppliedTokenStrategy(currentTokenStrategy)
      }

      this.votePageStore.setHasSuperVoterAccess(false)
      this.votePageStore.resetSuperVoterAllocations()
      this.votePageStore.setMaxLockEnabled(false)
      this.votePageStore.setLastAppliedStrategy(null)
      this.votePageStore.setLastAppliedTokenStrategy(null)
      this.votePageStore.clearSelectedEngine()
    })
  }

  private async handleWalletConnect(_event: WalletConnectedEvent): Promise<void> {
    // Start the query when wallet connects
    if (this.accessQuery) {
      this.accessQuery.start()
      // Immediately check access on connect
      this.accessQuery.forceExecute()

      // Reset SuperVoter state on new wallet connect
      runInAction(() => {
        this.votePageStore.resetSuperVoterAllocations()
        this.votePageStore.setMaxLockEnabled(false)
        this.votePageStore.setLastAppliedStrategy(null)
        this.votePageStore.setLastAppliedTokenStrategy(null)
        this.votePageStore.clearSelectedEngine()
        // Note: previousAppliedStrategy and previousAppliedTokenStrategy are NOT reset - will be cleared after reapply
      })
    }
  }

  /**
   * Handle SuperVoter access granted event
   * Immediately refresh access state after grant transaction
   */
  private handleAccessGranted(_event: SuperVoterAccessGrantedEvent): void {
    this.forceExecute()
  }

  /**
   * Handle SuperVoter access revoked event
   * Immediately refresh access state after revoke transaction
   */
  private handleAccessRevoked(_event: SuperVoterAccessRevokedEvent): void {
    this.forceExecute()
  }

  /**
   * Handle SuperVoter manager delegated event
   * Immediately refresh access state after delegate transaction
   */
  private handleManagerDelegated(_event: SuperVoterManagerDelegatedEvent): void {
    this.forceExecute()
  }

  /**
   * Handle NFT selection changed event
   * Automatically reapply previous strategy if it exists and strategies are ready
   * Handles both optimization strategies (Safe/Degen/Beast) and token strategies
   */
  private handleNftSelectionChanged(event: NftSelectionChangedEvent): void {
    // Only reapply if NFTs are selected
    if (event.selectedNftIds.length === 0) {
      return
    }

    // StrategyManagerStore may not be set yet if VoteOptimizer hasn't been created
    if (!this.strategyManagerStore) {
      return
    }

    const previousStrategy = this.votePageStore.previousAppliedStrategy
    if (previousStrategy) {
      const strategyResult = this.strategyManagerStore.getStrategyResult(previousStrategy)
      if (strategyResult?.distribution) {
        runInAction(() => {
          this.votePageStore.applyStrategy(previousStrategy, this.strategyManagerStore!)
          this.votePageStore.setPreviousAppliedStrategy(null)
        })
        return
      }
    }

    const previousTokenStrategy = this.votePageStore.previousAppliedTokenStrategy
    if (previousTokenStrategy) {
      const tokenResult = this.strategyManagerStore.calculateTokenStrategyResult(previousTokenStrategy)
      if (tokenResult?.distribution) {
        runInAction(() => {
          this.votePageStore.applyTokenStrategy(previousTokenStrategy, this.strategyManagerStore!)
          this.votePageStore.setPreviousAppliedTokenStrategy(null)
        })
      }
    }
  }
}
